import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, ShieldCheck } from 'lucide-react';
interface QuoteOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuoteOverlay({
  isOpen,
  onClose
}: QuoteOverlayProps) {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false
  });
  // Lock body scroll and handle Escape key
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);
  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormState('idle');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        consent: false
      });
    }
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setTimeout(() => setFormState('success'), 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const inputClass = 'w-full bg-transparent border-0 border-b border-white/20 py-3 px-0 text-sm text-white placeholder:text-white/40 focus:ring-0 focus:border-accent transition-colors resize-none';
  return <AnimatePresence data-id="element-472">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-473">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} className="absolute inset-0 bg-primary-dark/85 backdrop-blur-xl" onClick={onClose} data-id="element-474" />

          {/* Modal Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-[900px] bg-white/5 border border-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-475">
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors text-white/70 hover:text-white" aria-label="Close modal" data-id="element-476">
              <X size={20} data-id="element-477" />
            </button>

            {formState === 'success' ? <div className="text-center py-16" data-id="element-478">
                <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-479">
                  <ShieldCheck className="w-10 h-10 text-accent" data-id="element-480" />
                </div>
                <h3 className="text-3xl font-serif text-white mb-4" data-id="element-481">
                  Quote requested
                </h3>
                <p className="text-white/70 max-w-md mx-auto" data-id="element-482">
                  Thank you. We've received your details and our team will be in
                  touch shortly to discuss your project.
                </p>
                <button onClick={onClose} className="mt-8 bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-full transition-colors font-medium text-sm" data-id="element-483">
                  Close window
                </button>
              </div> : <>
                <div className="mb-10 text-center md:text-left pr-12" data-id="element-484">
                  <h2 className="text-4xl md:text-5xl font-serif text-white leading-[1.1] tracking-tight mb-4" data-id="element-485">
                    Request a{' '}
                    <span className="text-accent italic" data-id="element-486">free quote</span>
                  </h2>
                  <p className="text-white/60" data-id="element-487">
                    Get expert advice and a transparent, no-obligation quote for
                    your commercial HVAC needs.
                  </p>
                </div>

                <form className="space-y-8" onSubmit={handleSubmit} data-id="element-488">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-489">
                    <input id="firstName" name="firstName" type="text" placeholder="First name" required value={formData.firstName} onChange={handleChange} className={inputClass} data-id="element-490" />
                    <input id="lastName" name="lastName" type="text" placeholder="Last name" required value={formData.lastName} onChange={handleChange} className={inputClass} data-id="element-491" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-492">
                    <input id="email" name="email" type="email" placeholder="Email address" required value={formData.email} onChange={handleChange} className={inputClass} data-id="element-493" />
                    <input id="phone" name="phone" type="tel" placeholder="Phone number" required value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-494" />
                  </div>

                  <input id="company" name="company" type="text" placeholder="Company name" required value={formData.company} onChange={handleChange} className={inputClass} data-id="element-495" />

                  <textarea id="message" name="message" placeholder="Tell us about your project or requirements..." rows={3} value={formData.message} onChange={handleChange} className={inputClass} data-id="element-496" />

                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-4" data-id="element-497">
                    <div className="flex items-start gap-3 max-w-md" data-id="element-498">
                      <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleChange} className="mt-1 rounded-sm border-white/20 bg-transparent text-accent focus:ring-accent focus:ring-offset-primary-dark" data-id="element-499" />
                      <label htmlFor="consent" className="text-xs text-white/60 leading-relaxed" data-id="element-500">
                        I agree to the processing of my data in accordance with
                        the{' '}
                        <a href="/privacy" className="text-white underline underline-offset-2 hover:text-accent transition-colors" data-id="element-501">
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    <button type="submit" disabled={formState === 'loading'} className="w-full md:w-auto shrink-0 bg-accent hover:bg-accent/90 disabled:opacity-70 text-white text-sm font-medium py-4 px-10 rounded-full transition-colors flex items-center justify-center gap-3" data-id="element-502">
                      {formState === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-503" /> : <span data-id="element-504">Submit request</span>}
                    </button>
                  </div>
                </form>

                <div className="mt-10 pt-6 border-t border-white/10 text-center" data-id="element-505">
                  <p className="text-xs text-white/40 tracking-wide uppercase" data-id="element-506">
                    Daikin D1+ Premier Partner <span className="mx-2" data-id="element-507">·</span>{' '}
                    Constructionline Gold <span className="mx-2" data-id="element-508">·</span> 16+
                    years
                  </p>
                </div>
              </>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}